import {
  HeaderBalanceAccount,
  HeaderBalanceRule,
  HeaderBalanceSource,
  LIST_HEADER_BALANCE_RULES,
  mirrorBalanceRule,
  pettyCashBalanceRule,
} from '@celerofinancas/common-domain';

/**
 * A checking account plus the automatic-investment balance.
 *
 * `HeaderBalanceAccount` in `common-domain@5.0.1` predates the field, so the local rule
 * widens it. Reading the value is safe regardless: the core resolves the balance with
 * `Number(checkingAccount?.[source])` — a plain property index — so a source the union
 * does not list still resolves correctly at runtime. Verified in the shipped bundle
 * before relying on it.
 */
type InvestmentAwareAccount = HeaderBalanceAccount & {
  investment_value?: number | string | null;
};

/**
 * `'investment_value'` as a balance source.
 *
 * The cast is the counterpart of {@link InvestmentAwareAccount}: the union in this
 * version of the core lists three sources and not this one. It disappears with the
 * fleet upgrade, together with the local rule below.
 */
const INVESTMENT_VALUE_SOURCE = 'investment_value' as unknown as HeaderBalanceSource;

/**
 * Returns `'investment_value'` when the account holds a non-zero automatic-investment
 * balance, `null` otherwise.
 *
 * ## Why this lives here and not in the core
 *
 * The core ships this exact rule as `investmentValueBalanceRule`, but only from
 * `@celerofinancas/common-domain@5.2.0` on, and this project is on `5.0.1`. Bumping is
 * not a one-line change: `5.2.x` needs the Downloads feature that landed in
 * `ui-http-services@5.0.4+` and `ui-common@7.3.x`, and with those in place the bank
 * header stopped receiving its account list altogether — the consolidated total
 * rendered `R$ 0,00`, and `state-management@7.0.3` (the version
 * `serasa-cl-webcomponents` bumped to in its PR #331) did not close the gap either.
 *
 * So the rule is reproduced locally, deliberately and temporarily. **Delete this
 * function and import the core one** once the fleet upgrade lands — the signature is
 * identical, so the swap is one import line.
 *
 * ## The rule itself
 *
 * Card [86e30q97n](https://app.clickup.com/t/86e30q97n): zero means "no automatic
 * investment", not "a balance of zero", so it must delegate instead of printing
 * R$ 0,00. `null`, `undefined` and a non-numeric value delegate for the same reason.
 * The comparison goes through `Number` because the API sends amounts as strings in
 * some payloads.
 *
 * @param account checking account being resolved
 * @returns the investment source, or `null` to delegate to the next rule
 */
export const investmentValueBalanceRule: HeaderBalanceRule = (account) => {
  const { investment_value: investmentValue } = account as InvestmentAwareAccount;

  if (investmentValue === null || investmentValue === undefined) {
    return null;
  }

  const numeric = Number(investmentValue);

  return !Number.isNaN(numeric) && numeric !== 0 ? INVESTMENT_VALUE_SOURCE : null;
};

/**
 * Returns `'last_statement_balance'` when a statement date exists, `null` otherwise.
 *
 * Replaces the core's `currentStatementBalanceRule`, which only accepts a statement
 * from the current day (D-0). GetNet shows the statement balance as soon as it exists,
 * so an outdated statement is preferred over the current account balance — the same
 * decision `serasa-cl-webcomponents` took in
 * [PR #335](https://github.com/celerofinancas/serasa-cl-webcomponents/pull/335), which
 * aligned Serasa with CAIXA.
 *
 * @param account checking account being resolved
 * @returns the statement balance source, or `null` to delegate to the next rule
 */
export const localStatementBalanceRule: HeaderBalanceRule = (account) => {
  return account?.last_statement_at ? 'last_statement_balance' : null;
};

/**
 * Balance resolution for the bank header, the accounts modal and the accounts total —
 * card [86e30q97n](https://app.clickup.com/t/86e30q97n).
 *
 * ## The rule the card asks for
 *
 * An account with automatic investment keeps the real available balance in
 * `investment_value`: the surplus is swept into a daily-liquidity investment and
 * redeemed when the account would go negative. Showing `last_statement_balance` there
 * understates what the client sees in their Internet Banking, which is the complaint the
 * card opens with.
 *
 * ## Why the chain is pinned instead of left to the core
 *
 * The token **replaces** the core chain rather than merging with it, so every rule that
 * must stay active has to be listed here — the investment rule included. Omitting it
 * would silently switch the automatic investment balance back off, which is the trap
 * worth writing down.
 *
 * Order, matching the core's with one substitution at the end:
 *
 * 1. `pettyCashBalanceRule` — petty cash always shows its own balance, untouched by the
 *    investment rule;
 * 2. `mirrorBalanceRule` — kept for parity with the fleet, inert here because no GetNet
 *    account is whitelisted for a mirror balance (that is the Sicredi exception the card
 *    says to preserve);
 * 3. {@link investmentValueBalanceRule} — the rule this card is about;
 * 4. {@link localStatementBalanceRule} — the core's `currentStatementBalanceRule`
 *    without the D-0 check.
 *
 * ## What this does not reach
 *
 * The "Seu financeiro" dashboard total resolves its own balance inside the core and does
 * not read this token, so it keeps the date validation. Same situation as CAIXA and
 * Serasa today — Ryan flagged it on the card rather than working around it.
 */
export const LIST_HEADER_BALANCE_TOKENS = [
  {
    provide: LIST_HEADER_BALANCE_RULES,
    useValue: [pettyCashBalanceRule, mirrorBalanceRule, investmentValueBalanceRule, localStatementBalanceRule],
  },
];
